import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
} from '@mui/material';
import { Project } from '../../types';

interface TimelineViewProps {
  projects: Project[];
  timelineView: 'month' | 'quarter' | 'year';
  setTimelineView: (view: 'month' | 'quarter' | 'year') => void;
}

const TimelineView: React.FC<TimelineViewProps> = ({
  projects,
  timelineView,
  setTimelineView,
}) => {
  // Timeline helper functions
  const getTimelineColumns = () => {
    // Find the earliest start date and latest end date from all projects
    let earliestDate = new Date();
    let latestDate = new Date();
    
    projects.forEach(project => {
      if (project.startDate) {
        const startDate = new Date(project.startDate);
        if (startDate < earliestDate) earliestDate = startDate;
      }
      if (project.endDate) {
        const endDate = new Date(project.endDate);
        if (endDate > latestDate) latestDate = endDate;
      }
    });
    
    // Ensure we have at least some range to show
    if (earliestDate.getTime() === latestDate.getTime()) {
      latestDate = new Date(earliestDate);
      latestDate.setMonth(latestDate.getMonth() + 12);
    }
    
    const columns = [];
    
    if (timelineView === 'month') {
      let currentMonth = new Date(earliestDate.getFullYear(), earliestDate.getMonth());
      while (currentMonth <= latestDate) {
        columns.push(currentMonth.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }));
        currentMonth.setMonth(currentMonth.getMonth() + 1);
      }
    } else if (timelineView === 'quarter') {
      let currentQuarter = new Date(earliestDate.getFullYear(), Math.floor(earliestDate.getMonth() / 3) * 3);
      while (currentQuarter <= latestDate) {
        const quarter = Math.floor(currentQuarter.getMonth() / 3) + 1;
        columns.push(`Q${quarter} ${currentQuarter.getFullYear().toString().slice(-2)}`);
        currentQuarter.setMonth(currentQuarter.getMonth() + 3);
      }
    } else {
      for (let year = earliestDate.getFullYear(); year <= latestDate.getFullYear(); year++) {
        columns.push(year.toString());
      }
    }
    
    return columns;
  };

  const renderProjectTimeline = (project: Project) => {
    const columns = getTimelineColumns();
    const timelineStart = new Date();
    projects.forEach(p => {
      if (p.startDate) {
        const startDate = new Date(p.startDate);
        if (startDate < timelineStart) timelineStart.setTime(startDate.getTime());
      }
    });

    const referenceDate = new Date(timelineStart);
    referenceDate.setHours(0, 0, 0, 0);

    let startCol = 0;
    let duration = 2; // Default duration
    
    if (project.startDate && project.endDate) {
      const startDate = new Date(project.startDate);
      const endDate = new Date(project.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      if (timelineView === 'month') {
        startCol = (startDate.getFullYear() - referenceDate.getFullYear()) * 12 + 
                  (startDate.getMonth() - referenceDate.getMonth());
        duration = Math.max(1, (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                           (endDate.getMonth() - startDate.getMonth()) + 1);
      } else if (timelineView === 'quarter') {
        const startQuarter = Math.floor(startDate.getMonth() / 3);
        const refQuarter = Math.floor(referenceDate.getMonth() / 3);
        startCol = (startDate.getFullYear() - referenceDate.getFullYear()) * 4 + 
                  (startQuarter - refQuarter);
        const endQuarter = Math.floor(endDate.getMonth() / 3);
        duration = Math.max(1, (endDate.getFullYear() - startDate.getFullYear()) * 4 + 
                           (endQuarter - startQuarter) + 1);
      } else {
        startCol = startDate.getFullYear() - referenceDate.getFullYear();
        duration = Math.max(1, endDate.getFullYear() - startDate.getFullYear() + 1);
      }
    } else if (project.startDate) {
      const startDate = new Date(project.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      if (timelineView === 'month') {
        startCol = (startDate.getFullYear() - referenceDate.getFullYear()) * 12 + 
                  (startDate.getMonth() - referenceDate.getMonth());
        duration = Math.max(1, Math.ceil(3 * (1 - project.progress / 100)));
      } else if (timelineView === 'quarter') {
        const startQuarter = Math.floor(startDate.getMonth() / 3);
        const refQuarter = Math.floor(referenceDate.getMonth() / 3);
        startCol = (startDate.getFullYear() - referenceDate.getFullYear()) * 4 + 
                  (startQuarter - refQuarter);
        duration = Math.max(1, Math.ceil(3 * (1 - project.progress / 100)));
      } else {
        startCol = startDate.getFullYear() - referenceDate.getFullYear();
        duration = Math.max(1, Math.ceil(3 * (1 - project.progress / 100)));
      }
    }

    const projectColor = project.status === 'Completed' ? '#f59f1f' : '#2c2c2c';

    return (
      <Box key={project.id} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: { xs: 120, md: 200 },
            pr: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          <Typography variant="body2" sx={{ 
            fontWeight: 'bold', 
            color: '#2c2c2c',
            fontSize: { xs: '0.75rem', md: '0.875rem' }
          }}>
            {project.name}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', height: 30, position: 'relative', backgroundColor: '#fafafa', borderRadius: 1 }}>
          {columns.map((_, colIndex) => (
            <Box
              key={colIndex}
              sx={{
                flex: 1,
                borderRight: '1px solid #e8e8e8',
                position: 'relative'
              }}
            >
              {colIndex >= startCol && colIndex < startCol + duration && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: colIndex === startCol ? 2 : 0,
                    right: colIndex === startCol + duration - 1 ? 2 : 0,
                    top: 2,
                    bottom: 2,
                    backgroundColor: projectColor,
                    borderRadius: colIndex === startCol ? '4px 0 0 4px' : colIndex === startCol + duration - 1 ? '0 4px 4px 0' : 0,
                    opacity: 0.85,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                >
                  {colIndex === startCol && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold',
                        fontSize: '0.65rem',
                        px: 0.5
                      }}
                    >
                      {project.progress}%
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ 
          color: '#2c2c2c', 
          fontWeight: 'bold',
          fontSize: { xs: '1.5rem', md: '2.125rem' }
        }}>
          Project Timeline & Gantt Chart
        </Typography>
      </Box>
      <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, md: 2 },
          overflowX: { xs: 'auto', md: 'visible' },
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}>
          {(['month', 'quarter', 'year'] as const).map((view) => (
            <Button
              key={view}
              onClick={() => setTimelineView(view)}
              sx={{
                pb: 1,
                px: { xs: 1.5, md: 2 },
                borderRadius: 0,
                borderBottom: timelineView === view ? '3px solid #f59f1f' : '3px solid transparent',
                color: timelineView === view ? '#f59f1f' : '#666',
                fontWeight: timelineView === view ? 'bold' : 'normal',
                backgroundColor: 'transparent',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                whiteSpace: 'nowrap',
                minWidth: 'auto',
                '&:hover': { backgroundColor: 'rgba(245, 159, 31, 0.05)' }
              }}
            >
              {view.charAt(0).toUpperCase() + view.slice(1)}
            </Button>
          ))}
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ 
        mb: 2,
        fontSize: { xs: '0.75rem', md: '0.875rem' }
      }}>
        Explore project timelines, visualize task schedules, and identify overlaps or bottlenecks.
      </Typography>

      <Paper elevation={0} sx={{ 
        overflow: 'auto', 
        border: '1px solid #e0e0e0', 
        boxShadow: 'none',
        WebkitOverflowScrolling: 'touch'
      }}>
        <Box sx={{ minWidth: { xs: 800, md: 1200 }, p: { xs: 2, md: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 1.5, md: 3 }, 
            mb: 3, 
            pb: 2, 
            borderBottom: '1px solid #e0e0e0' 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, backgroundColor: '#f59f1f', borderRadius: '4px' }} />
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 500, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                Completed Projects
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, backgroundColor: '#2c2c2c', borderRadius: '4px' }} />
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 500, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                In Progress Projects
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', mb: 2 }}>
            <Box sx={{ width: { xs: 120, md: 200 }, pr: 2 }} />
            <Box sx={{ flex: 1, display: 'flex', gap: 0 }}>
              {getTimelineColumns().map((col, idx) => (
                <Box
                  key={idx}
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                    fontWeight: 'bold',
                    color: '#2c2c2c',
                    pb: 1,
                    borderBottom: '2px solid #e0e0e0'
                  }}
                >
                  {col}
                </Box>
              ))}
            </Box>
          </Box>

          {projects.map(project => renderProjectTimeline(project))}
        </Box>
      </Paper>
    </Container>
  );
};

export default TimelineView;
